import type { Profile, Subscription } from "@/types";
import { isSubscriptionBlocking } from "@/types/subscription.types";
import { fetchOwnProfile } from "@a/profiles";
import { fetchOwnSubscription } from "@a/subscriptions";
import { logAuthFailure, logSecurityEvent } from "@a/security";
import { buildRoute } from "@u/routes";
import { supabase } from "@cl/supabase";
import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import i18n from "@t/i18n";
import { AuthContext } from "./AuthContexts";

export interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  subscription: Subscription | null;
  loadingAuth: boolean;
  loadingSubscription: boolean;
  isAdmin: boolean;
  products: string[];
  canCreateTour: boolean;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    name?: string,
    deviceFingerprint?: string,
    captchaToken?: string,
  ) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (
    email: string,
    options?: { redirectTo: string },
  ) => Promise<{ error: Error | null }>;
  updatePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<{ error: string | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [stripeSubscription, setStripeSubscription] = useState<Subscription | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  const refreshSubscription = useCallback(async () => {
    setLoadingSubscription(true);
    try {
      const sub = await fetchOwnSubscription();
      setStripeSubscription(sub);
    } catch (e) {
      console.warn("[Auth] Subscription error:", e);
      setStripeSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  }, []);


  const setAuth = useCallback(
    async (session: Session | null) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        try {
          const prof = await fetchOwnProfile(currentUser.id);
          // Suspended = hard lockout. Kill the session immediately; RLS/hooks are
          // the server-side barrier, this is the client half. Bail before loading
          // any account data.
          if (prof?.role === "suspended") {
            await supabase.auth.signOut();
            queryClient.clear();
            setUser(null);
            setProfile(null);
            setLoadingAuth(false);
            toast.error(i18n.t("account_suspended"));
            return;
          }
          setProfile(prof);
          if (prof?.role !== "admin") await refreshSubscription();
        } catch (e) {
          console.warn("[Auth] Profile error:", e);
          setProfile(null);
        }
      } else {
        setProfile(null);
        setStripeSubscription(null);
        // New/anon session — drop any prior user's cached queries.
        queryClient.clear();
      }

      setLoadingAuth(false);
    },
    [queryClient, refreshSubscription],
  );

  // onAuthStateChange dispara INITIAL_SESSION al montarse —
  // no necesitamos getSession separado, evitamos doble fetch de profile.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuth(session);
    });
    return () => subscription.unsubscribe();
  }, [setAuth]);

  // ── Auth actions ──────────────────────────────────────────────────────

  // signIn no actualiza el estado directamente —
  // onAuthStateChange captura el evento SIGNED_IN automáticamente.
  const signIn = useCallback(async (
    email: string,
    password: string,
    captchaToken?: string,
  ) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });
    if (error) {
      console.error("[Auth] signIn error:", error);
      logAuthFailure("signin", email, error.message);
    } else {
      void logSecurityEvent("auth.signin.success", "info", {
        email: email.trim().toLowerCase(),
      });
    }
    return { error: error as Error | null };
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    name?: string,
    deviceFingerprint?: string,
    captchaToken?: string,
  ) => {
    const metadata: Record<string, string> = {};
    if (name) metadata.name = name;
    if (deviceFingerprint) metadata.device_fingerprint = deviceFingerprint;

    const hasData = Object.keys(metadata).length > 0;
    // Consent is recorded server-side in handle_new_user (SECURITY DEFINER) —
    // a client insert here fails RLS when email confirmation is on (no session yet).
    const { error } = await supabase.auth.signUp({
      email,
      password,
      ...(hasData || captchaToken
        ? {
            options: {
              ...(hasData ? { data: metadata } : {}),
              ...(captchaToken ? { captchaToken } : {}),
            },
          }
        : {}),
    });
    if (error) {
      console.error("[Auth] signUp error:", error);
      logAuthFailure("signup", email, error.message);
    }
    return { error: error as Error | null };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const base = (import.meta.env.VITE_APP_DOMAIN ?? window.location.origin).replace(/\/$/, "");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${base}${buildRoute.authCallback()}`,
      },
    });
    if (error) {
      console.error("[Auth] signInWithGoogle error:", error);
      logAuthFailure("oauth", undefined, error.message);
    }
    return { error: error as Error | null };
  }, []);

  const signOut = useCallback(async () => {
    setLoadingAuth(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn("[Auth] signOut error:", error.message);
      }
    } finally {
      // onAuthStateChange captura SIGNED_OUT pero limpiamos
      // manualmente para garantizar consistencia inmediata.
      setUser(null);
      setProfile(null);
      // Drop the previous user's cached data so a next login can't flash it.
      queryClient.clear();
      setLoadingAuth(false);
    }
  }, [queryClient]);

  const resetPassword = useCallback(async (
    email: string,
    options?: { redirectTo: string },
  ) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, options);
    if (error) {
      console.error("[Auth] resetPassword error:", error);
    }
    return { error: error as Error | null };
  }, []);

  const updatePassword = useCallback(async (
    currentPassword: string,
    newPassword: string,
  ) => {
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    if (!currentUser?.email) {
      return { error: "No authenticated user" };
    }

    const { error: reAuthError } = await supabase.auth.signInWithPassword({
      email: currentUser.email,
      password: currentPassword,
    });
    if (reAuthError) {
      console.error("[Auth] updatePassword reAuth error:", reAuthError);
      logAuthFailure("reauth", currentUser.email, reAuthError.message);
      return { error: reAuthError.message };
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (updateError) {
      console.error("[Auth] updatePassword error:", updateError);
    }
    return { error: updateError?.message ?? null };
  }, []);

  const updateEmail = useCallback(async (newEmail: string) => {
    localStorage.setItem("authCallbackNext", buildRoute.settings());
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: `${window.location.origin}${buildRoute.authCallback()}` },
    );
    if (error) {
      localStorage.removeItem("authCallbackNext");
      console.error("[Auth] updateEmail error:", error);
    }
    return { error: error?.message ?? null };
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const prof = await fetchOwnProfile();
      setProfile(prof);
    } catch (e: unknown) {
      const err = e as { message?: string };
      console.warn("[Auth] refreshProfile failed:", err.message);
    }
  }, []);

  const isAdmin = useMemo(() => profile?.role === "admin", [profile]);

  const products = useMemo<string[]>(() => {
    if (isAdmin) return ["360"];
    // 360 (free tier) es el producto real de toda cuenta; la columna products[]
    // sólo lleva extras opt-in.
    const extra = (profile?.products ?? []).filter((p) => p !== "360");
    return ["360", ...extra];
  }, [isAdmin, profile]);

  const canCreateTour = useMemo(
    () => isAdmin || !isSubscriptionBlocking(stripeSubscription),
    [isAdmin, stripeSubscription],
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      profile,
      subscription: stripeSubscription,
      loadingAuth,
      loadingSubscription,
      isAdmin,
      products,
      canCreateTour,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      updatePassword,
      updateEmail,
      refreshProfile,
      refreshSubscription,
    }),
    [
      user,
      profile,
      stripeSubscription,
      loadingAuth,
      loadingSubscription,
      isAdmin,
      products,
      canCreateTour,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      updatePassword,
      updateEmail,
      refreshProfile,
      refreshSubscription,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
